import { useQuery } from '@tanstack/react-query'
import {
  ArrowUpRight,
  CloudSnow,
  Compass,
  Moon,
  Snowflake,
  Sparkles,
  Sun,
  Thermometer,
  TrendingUp,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getDestination } from '../../lib/api'
import { Badge, Card, CardContent, CardHeader, CardTitle, NadiaLoader } from '../ui'
import { ChartProvenanceCallout } from '../ChartProvenanceCallout'

interface SeasonInfo {
  icon: string
  tag: { es: string; en: string; fi: string }
  nature: { es: string; en: string; fi: string }
  opportunity: { es: string; en: string; fi: string }
}

const MONTH_SEASONS: Record<number, SeasonInfo> = {
  1: {
    icon: '❄️',
    tag: { es: 'Invierno polar', en: 'Polar Winter', fi: 'Kaamos & Talvi' },
    nature: {
      es: 'Nieve profunda, noche polar y esquí alpino',
      en: 'Deep snow, polar night & downhill skiing',
      fi: 'Syvä lumi, kaamos ja laskettelu',
    },
    opportunity: {
      es: 'Maximizar ticket medio, cenas gourmet y servicios premium',
      en: 'Maximize average spend, cozy dining & premium add-ons',
      fi: 'Maksimoi keskiostos, illalliset ja premium-lisäpalvelut',
    },
  },
  2: {
    icon: '❄️',
    tag: { es: 'Temporada alta', en: 'Peak Winter', fi: 'Huipputalvi' },
    nature: {
      es: 'Pleno invierno y vacaciones escolares europeas',
      en: 'Peak winter conditions & European school breaks',
      fi: 'Täysi talvikausi ja Euroopan talvilomat',
    },
    opportunity: {
      es: 'Paquetes completos de nieve, trineos husky y motos de nieve',
      en: 'Comprehensive snow packages, huskies & snowmobiles',
      fi: 'Kokonaiset talvipaketit, huskyvaljakot ja moottorikelkat',
    },
  },
  3: {
    icon: '🎿',
    tag: { es: 'Sol y nieve', en: 'Spring Skiing', fi: 'Keväthanget' },
    nature: {
      es: 'Días largos y luminosos sobre nieve perfecta',
      en: 'Bright long days on crisp spring snow',
      fi: 'Aurinkoiset päivät ja keväthanget',
    },
    opportunity: {
      es: 'Esquí de fondo, terrazas soleadas y escapadas activas',
      en: 'Cross-country skiing, sunny terraces & active breaks',
      fi: 'Murtomaahiihto, aurinkoterassit ja aktiivilomat',
    },
  },
  4: {
    icon: '💧',
    tag: { es: 'Inicio deshielo', en: 'Early Thaw', fi: 'Kevätkausi' },
    nature: {
      es: 'Cierre de pistas y comienzo del deshielo primaveral',
      en: 'Slopes close and spring thaw begins',
      fi: 'Rinteet sulkeutuvat ja lumet alkavat sulaa',
    },
    opportunity: {
      es: 'Ofertas de desconexión, tranquilidad y bienestar nórdico',
      en: 'Quiet getaways, relaxation & Nordic wellness deals',
      fi: 'Rauhoittumislomat, hiljaisuus ja hyvinvointitarjoukset',
    },
  },
  5: {
    icon: '🌱',
    tag: { es: 'Valle de primavera', en: 'Spring Valley', fi: 'Kevään suvantovaihe' },
    nature: {
      es: 'Renacer de la taiga y ríos en pleno deshielo',
      en: 'Forest awakes and rivers thaw completely',
      fi: 'Luonnon herääminen ja jokien sulaminen',
    },
    opportunity: {
      es: 'Mantenimiento de negocio y preventa anticipada de invierno',
      en: 'Business prep & launch winter early-bird campaigns',
      fi: 'Yrityksen huoltokausi ja talven ennakkomyynnin aloitus',
    },
  },
  6: {
    icon: '☀️',
    tag: { es: 'Sol de medianoche', en: 'Midnight Sun', fi: 'Yötön yö' },
    nature: {
      es: '24 horas de luz continua y lagos cristalinos',
      en: '24 hours of continuous daylight & clear lakes',
      fi: 'Yötön yö ja 24 tunnin valoisuus',
    },
    opportunity: {
      es: 'Trekking nocturno, paseos en canoa y eventos al aire libre',
      en: 'Midnight hiking, canoeing & outdoor nature events',
      fi: 'Yövaellukset, melonta ja ulkoilmatapahtumat',
    },
  },
  7: {
    icon: '🌿',
    tag: { es: 'Verano ártico', en: 'Arctic Summer', fi: 'Arktinen kesä' },
    nature: {
      es: 'Temperaturas suaves, senderismo y rutas en bicicleta',
      en: 'Mild temperatures, mountain biking & trail hiking',
      fi: 'Leuto sää, maastopyöräily ja vaellusreitit',
    },
    opportunity: {
      es: 'Turismo familiar, alquiler de bicicletas y rutas autoguiadas',
      en: 'Family trips, bike rentals & self-guided nature tours',
      fi: 'Perhematkailu, pyörävuokraus ja luontoretket',
    },
  },
  8: {
    icon: '🫐',
    tag: { es: 'Cosecha del bosque', en: 'Forest Harvest', fi: 'Satokausi' },
    nature: {
      es: 'Recolección de bayas silvestres, pesca y tardes frescas',
      en: 'Wild berry foraging, fly fishing & cool evenings',
      fi: 'Marjastus, kalastus ja raikkaat loppukesän illat',
    },
    opportunity: {
      es: 'Experiencias de gastronomía silvestre y saunas tradicionales',
      en: 'Wild foraging dinners & traditional sauna experiences',
      fi: 'Lähiruokaelämykset ja perinteiset saunat',
    },
  },
  9: {
    icon: '🍂',
    tag: { es: 'Ruska / Otoño', en: 'Ruska Autumn', fi: 'Ruska-aika' },
    nature: {
      es: 'Colores otoñales espectaculares y regreso de noches oscuras',
      en: 'Vibrant golden foliage & return of dark starry skies',
      fi: 'Upea maaruska ja puiden väriloisto',
    },
    opportunity: {
      es: 'Fotografía de naturaleza y primeras auroras boreales',
      en: 'Nature photography & early aurora excursions',
      fi: 'Luontokuvaus ja alkukauden revontuliretket',
    },
  },
  10: {
    icon: '🌌',
    tag: { es: 'Temporada de Auroras', en: 'Northern Lights', fi: 'Revontulikausi' },
    nature: {
      es: 'Noches oscuras, cielos estrellados y primeras heladas',
      en: 'Dark nights, crisp starry skies & first frost',
      fi: 'Pimeät yöt, tähtitaivas ja ensipakkaset',
    },
    opportunity: {
      es: 'Tours de auroras boreales y captación de reservas de invierno',
      en: 'Aurora hunting & capturing winter advance bookings',
      fi: 'Revontuliretket ja talvisesongin ennakkovaraukset',
    },
  },
  11: {
    icon: '❄️',
    tag: { es: 'Primeras nieves', en: 'First Snow', fi: 'Ensilumi' },
    nature: {
      es: 'Primeras nevadas persistentes y apertura de pistas tempranas',
      en: 'First persistent snow & early ski slope openings',
      fi: 'Ensilumi ja varhaisten rinteiden avaus',
    },
    opportunity: {
      es: 'Promociones de inicio de esquí y escapadas prenavideñas',
      en: 'Early season ski packages & pre-holiday getaways',
      fi: 'Kaudenavaustarjoukset ja pikkujoulukauden paketit',
    },
  },
  12: {
    icon: '🎅',
    tag: { es: 'Navidad y magia', en: 'Christmas Season', fi: 'Joulusesonki' },
    nature: {
      es: 'Nieve profunda de cuento, frío polar y ambiente navideño',
      en: 'Fairytale deep snow, crisp cold & holiday spirit',
      fi: 'Satumainen tykkylumi ja joulutunnelma',
    },
    opportunity: {
      es: 'Precios máximos del año, paquetes de Papá Noel y alta ocupación',
      en: 'Yearly peak pricing, Santa packages & full occupancy',
      fi: 'Vuoden korkeimmat hinnat, joulupaketit ja täysi tupa',
    },
  },
}

const MONTHS: Record<string, string[]> = {
  es: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  fi: ['Tam', 'Hel', 'Maa', 'Huh', 'Tou', 'Kes', 'Hei', 'Elo', 'Syy', 'Lok', 'Mar', 'Jou'],
}

const MONTH_NAMES_FULL: Record<string, string[]> = {
  es: [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ],
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  fi: [
    'Tammikuu',
    'Helmikuu',
    'Maaliskuu',
    'Huhtikuu',
    'Toukokuu',
    'Kesäkuu',
    'Heinäkuu',
    'Elokuu',
    'Syyskuu',
    'Lokakuu',
    'Marraskuu',
    'Joulukuu',
  ],
}

/**
 * Panel unificado de señales de destino e inteligencia de mercado para PYMEs:
 * 1. Diagnóstico del momento actual (contexto de estacionalidad y foco de negocio).
 * 2. Gatillos tácticos inmediatos (clima real traducido a acciones comerciales hoy).
 * 3. Referencia de estacionalidad de 12 meses (curva de ocupación, precio y atractivo natural).
 */
export function DestinationSignalsPanel() {
  const { t, i18n } = useTranslation()
  const rawLocale = (i18n.language || 'es').slice(0, 2)
  const locale = (['es', 'en', 'fi'].includes(rawLocale) ? rawLocale : 'es') as 'es' | 'en' | 'fi'

  const monthsShort = MONTHS[locale] ?? MONTHS.es
  const monthsFull = MONTH_NAMES_FULL[locale] ?? MONTH_NAMES_FULL.es

  const { data, isLoading, isError } = useQuery({
    queryKey: ['destination'],
    queryFn: getDestination,
    staleTime: 10 * 60_000,
  })

  // Mes actual detectado según la fecha devuelta por la API o el reloj del sistema
  const currentMonth = useMemo(() => {
    if (data?.weather?.date) {
      const d = new Date(data.weather.date)
      if (!isNaN(d.getTime())) return d.getMonth() + 1
    }
    return new Date().getMonth() + 1
  }, [data?.weather?.date])

  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth)

  // Sincronizar el mes seleccionado por defecto con el mes actual
  useMemo(() => {
    if (currentMonth) setSelectedMonth(currentMonth)
  }, [currentMonth])

  if (isLoading) {
    return (
      <NadiaLoader
        minHeight="min-h-[320px]"
        message={t('loading.loadingSignals', 'Cargando señales meteorológicas y estacionales de Levi...')}
        subtitle={t('loading.nadiaProcessing', 'Consultando modelos analíticos de Laponia...')}
      />
    )
  }
  if (isError || !data) return null

  const w = data.weather
  const maxOcc = Math.max(...data.monthly.map((m) => m.occupancy ?? 0), 1)

  const activeMonthData = data.monthly.find((m) => m.month === selectedMonth)
  const activeSeasonInfo = MONTH_SEASONS[selectedMonth] ?? MONTH_SEASONS[10]

  const currentMonthData = data.monthly.find((m) => m.month === currentMonth)
  const currentSeasonInfo = MONTH_SEASONS[currentMonth] ?? MONTH_SEASONS[10]

  // Próximo mes relevante para previsión a 30-60 días
  const nextPeakMonthData = data.monthly.find((m) => m.month === 12)

  return (
    <Card className="border bg-card/60 backdrop-blur-sm overflow-hidden shadow-sm">
      <CardHeader className="pb-3 border-b border-border/40 bg-muted/10">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-sky-500/10 text-sky-400">
              <Compass className="size-4" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold">
                {t('destination.title', 'Señales del destino y contexto de mercado (Kittilä)')}
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {t(
                  'destination.sub',
                  'Condiciones en tiempo real y guía de estacionalidad anual para guiar tus decisiones comerciales.',
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs gap-1 border-amber-500/30 text-amber-300 bg-amber-500/5">
              <Sparkles className="size-3 text-amber-400" />
              <span>
                {monthsFull[currentMonth - 1]}: {currentSeasonInfo.tag[locale]}
              </span>
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 pt-5">
        {/* ============================================================== */}
        {/* BLOQUE 1: DIAGNÓSTICO DEL MOMENTO ACTUAL ("ESTÁS AQUÍ")       */}
        {/* ============================================================== */}
        <div className="rounded-xl border border-primary/20 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant="default" className="text-[11px] font-semibold tracking-wide">
                  {t('destination.youAreHere', 'ESTÁS AQUÍ')} · {monthsFull[currentMonth - 1].toUpperCase()}
                </Badge>
                <span className="text-xs font-medium text-foreground/80 flex items-center gap-1">
                  <span>{currentSeasonInfo.icon}</span> {currentSeasonInfo.nature[locale]}
                </span>
              </div>
              <p className="text-sm text-foreground/90 font-medium pt-1">
                {locale === 'es' &&
                  `Fase de valle otoñal con ocupación contenida (${Math.round(currentMonthData?.occupancy ?? 0)}%) y precio medio de ${Math.round(currentMonthData?.median_price ?? 0)}€.`}
                {locale === 'en' &&
                  `Autumn valley phase with moderate occupancy (${Math.round(currentMonthData?.occupancy ?? 0)}%) and average price of ${Math.round(currentMonthData?.median_price ?? 0)}€.`}
                {locale === 'fi' &&
                  `Syksyn suvantovaihe: maltillinen käyttöaste (${Math.round(currentMonthData?.occupancy ?? 0)}%) ja keskihinta ${Math.round(currentMonthData?.median_price ?? 0)}€.`}
              </p>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 pt-0.5">
                <ArrowUpRight className="size-3.5 text-amber-400 shrink-0" />
                <span>
                  {locale === 'es' &&
                    `Recomendación táctica: ${currentSeasonInfo.opportunity[locale]}. En diciembre la ocupación sube al ${Math.round(nextPeakMonthData?.occupancy ?? 0)}% con precios de ~${Math.round(nextPeakMonthData?.median_price ?? 0)}€: abre ya la venta anticipada de invierno.`}
                  {locale === 'en' &&
                    `Tactical advice: ${currentSeasonInfo.opportunity[locale]}. In December occupancy surges to ${Math.round(nextPeakMonthData?.occupancy ?? 0)}% with rates around ${Math.round(nextPeakMonthData?.median_price ?? 0)}€: open early-bird winter booking now.`}
                  {locale === 'fi' &&
                    `Taktinen vinkki: ${currentSeasonInfo.opportunity[locale]}. Joulukuussa käyttöaste nousee ${Math.round(nextPeakMonthData?.occupancy ?? 0)}%:iin hintatasolla ~${Math.round(nextPeakMonthData?.median_price ?? 0)}€.`}
                </span>
              </p>
            </div>

            {/* Micro métricas del mes actual */}
            <div className="flex items-center gap-3 shrink-0 bg-background/50 border border-border/50 rounded-lg p-2.5">
              <div className="text-center px-2">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground block">
                  {t('destination.occ', 'Ocupación est.')}
                </span>
                <span className="text-base font-bold font-mono text-amber-400">
                  {Math.round(currentMonthData?.occupancy ?? 0)}%
                </span>
              </div>
              <div className="h-7 w-px bg-border/60" />
              <div className="text-center px-2">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground block">
                  {t('destination.price', 'Precio mediano')}
                </span>
                <span className="text-base font-bold font-mono text-emerald-400">
                  {currentMonthData?.median_price ? `${Math.round(currentMonthData.median_price)}€` : '—'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* BLOQUE 2: GATILLOS TÁCTICOS METEOROLÓGICOS (HOY / 7 DÍAS)     */}
        {/* ============================================================== */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <CloudSnow className="size-3.5 text-sky-400" />
              {t('destination.triggersTitle', 'Gatillos de marketing en tiempo real (Clima en Kittilä)')}
            </span>
            <span className="text-[11px] text-emerald-400 flex items-center gap-1 font-mono">
              <span className="size-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {t('destination.realData', 'Dato real Open-Meteo')}
            </span>
          </div>

          {w ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
              {/* Tarjeta 1: Temperatura */}
              <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-3 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-muted-foreground">
                    <span>{t('destination.temp', 'Temperatura')}</span>
                    <Thermometer className="size-3.5 text-emerald-400" />
                  </div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <p className="text-xl font-bold font-mono">
                      {w.temp_min != null && w.temp_max != null
                        ? `${Math.round(w.temp_min)}° / ${Math.round(w.temp_max)}°`
                        : '—'}
                    </p>
                    <span className="text-[10px] text-muted-foreground font-mono">{w.date}</span>
                  </div>
                </div>
                <div className="mt-2.5 pt-2 border-t border-emerald-500/20 text-[11px] text-emerald-300/90 leading-tight">
                  <span className="font-semibold text-emerald-300">{t('destination.triggerPrefix', 'Gatillo: ')}</span>
                  {w.temp_min != null && w.temp_min <= 5
                    ? locale === 'es'
                      ? 'Frío otoñal: promociona saunas de leña y gastronomía reconfortante.'
                      : locale === 'fi'
                        ? 'Syyspakkanen: markkinoi savusaunoja ja lämmintä lähiruokaa.'
                        : 'Autumn chill: highlight cozy saunas and warm local dining.'
                    : locale === 'es'
                      ? 'Días suaves: actividades de naturaleza y paseos al aire libre.'
                      : locale === 'fi'
                        ? 'Leuto sää: ulkoilu- ja luontoretket tuntureilla.'
                        : 'Mild days: outdoor trail walks and fresh air activities.'}
                </div>
              </div>

              {/* Tarjeta 2: Nieve 7d */}
              <div className="rounded-xl border border-sky-500/25 bg-sky-500/5 p-3 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-muted-foreground">
                    <span>{t('destination.snow', 'Nieve acumulada (7d)')}</span>
                    <Snowflake className="size-3.5 text-sky-400" />
                  </div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <p className="text-xl font-bold font-mono">{w.snowfall_7d} mm</p>
                    <Badge variant={w.snow_alert ? 'default' : 'secondary'} className="text-[9px] py-0 px-1.5 h-4">
                      {w.snow_alert ? t('destination.snowAlert', 'Con nieve') : t('destination.noSnow', 'Sin nevadas')}
                    </Badge>
                  </div>
                </div>
                <div className="mt-2.5 pt-2 border-t border-sky-500/20 text-[11px] text-sky-300/90 leading-tight">
                  <span className="font-semibold text-sky-300">{t('destination.triggerPrefix', 'Gatillo: ')}</span>
                  {w.snow_alert
                    ? locale === 'es'
                      ? '¡Primera nieve! Activa ofertas de apertura de pistas y esquí.'
                      : locale === 'fi'
                        ? 'Lunta maassa! Käynnistä rinteiden avaus- ja hiihtopakettien myynti.'
                        : 'Snow reported! Launch early season ski and winter thrill promos.'
                    : locale === 'es'
                      ? 'Aún sin nieve: enfoca en paisajes de taiga y preventa de invierno.'
                      : locale === 'fi'
                        ? 'Ei vielä pysyvää lunta: korosta syysluontoa ja talven ennakkovarauksia.'
                        : 'No snow yet: emphasize crisp autumn nature and winter bookings.'}
                </div>
              </div>

              {/* Tarjeta 3: Nubosidad & Auroras */}
              <div className="rounded-xl border border-indigo-500/25 bg-indigo-500/5 p-3 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-muted-foreground">
                    <span>{t('destination.cloud', 'Nubosidad & Auroras')}</span>
                    <Moon className="size-3.5 text-indigo-400" />
                  </div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <p className="text-xl font-bold font-mono">
                      {w.cloud_cover_avg != null ? `${Math.round(w.cloud_cover_avg)}%` : '—'}
                    </p>
                    <Badge
                      variant={w.aurora_alert ? 'default' : 'secondary'}
                      className="text-[9px] py-0 px-1.5 h-4"
                    >
                      {w.aurora_alert ? t('destination.clearSkies', 'Cielos claros') : t('destination.overcast', 'Cubierto')}
                    </Badge>
                  </div>
                </div>
                <div className="mt-2.5 pt-2 border-t border-indigo-500/20 text-[11px] text-indigo-300/90 leading-tight">
                  <span className="font-semibold text-indigo-300">{t('destination.triggerPrefix', 'Gatillo: ')}</span>
                  {w.aurora_alert
                    ? locale === 'es'
                      ? '¡Cielos despejados! Lanza alerta flash de tours de auroras boreales.'
                      : locale === 'fi'
                        ? 'Selkeä taivas! Lähetä pikaheräte illan revontuliretkille.'
                        : 'Clear skies! Send flash notifications for aurora hunting tours.'
                    : locale === 'es'
                      ? 'Nubes densas hoy: enfoca en planes de interior y experiencias gastronómicas.'
                      : locale === 'fi'
                        ? 'Paljon pilviä: suosittele sisäaktiviteetteja ja illalliselämyksiä.'
                        : 'Dense cloud cover: pivot to cozy indoor dining & cultural evening plans.'}
                </div>
              </div>

              {/* Tarjeta 4: Precipitación acumulada */}
              <div className="rounded-xl border border-border/70 bg-muted/25 p-3 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-muted-foreground">
                    <span>{t('destination.precip', 'Precipitación (7d)')}</span>
                    <Sun className="size-3.5 text-amber-400" />
                  </div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <p className="text-xl font-bold font-mono">{w.precipitation_7d} mm</p>
                    <span className="text-[10px] text-muted-foreground">{t('destination.accumulated', 'última semana')}</span>
                  </div>
                </div>
                <div className="mt-2.5 pt-2 border-t border-border/50 text-[11px] text-muted-foreground leading-tight">
                  <span className="font-semibold text-foreground/80">{t('destination.contextPrefix', 'Contexto: ')}</span>
                  {locale === 'es'
                    ? 'Humedad ambiental estable. Ideal para calzado de senderismo y ropa técnica de entretiempo.'
                    : locale === 'fi'
                      ? 'Vakaa kosteus. Suosittele kerrospukeutumista ja maastokenkiä retkeilyyn.'
                      : 'Stable humidity levels. Recommend proper trail footwear and layered outdoor gear.'}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* ============================================================== */}
        {/* BLOQUE 3: CURVA DE ESTACIONALIDAD ANUAL (12 MESES)            */}
        {/* ============================================================== */}
        <div className="space-y-3 pt-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-400">
                <TrendingUp className="size-3.5" />
                {t('destination.monthlyRef', 'Curva de estacionalidad anual del destino (12 meses)')}
              </span>
              <p className="text-[11px] text-muted-foreground">
                {t(
                  'destination.interactiveHint',
                  'Haz clic en cualquier mes para explorar su ocupación, precio mediano y atractivo natural característico.',
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-400">
                {t('destination.estimatedData', 'Simulado sobre Data Pack')}
              </Badge>
            </div>
          </div>

          {/* Gráfica de 12 meses */}
          <div className="rounded-xl border border-border/50 bg-muted/15 p-3">
            <div className="grid grid-cols-12 gap-1.5 h-36 pt-4">
              {data.monthly.map((m) => {
                const isCurrent = m.month === currentMonth
                const isSelected = m.month === selectedMonth
                const pct = Math.round(((m.occupancy ?? 0) / maxOcc) * 100)
                const season = MONTH_SEASONS[m.month] ?? MONTH_SEASONS[1]

                return (
                  <button
                    key={m.month}
                    type="button"
                    onClick={() => setSelectedMonth(m.month)}
                    className={`relative flex flex-col items-center justify-end h-full gap-1 p-1 rounded-lg transition-all text-left group cursor-pointer ${
                      isSelected
                        ? 'bg-amber-500/15 ring-2 ring-amber-400 shadow-sm'
                        : isCurrent
                          ? 'bg-primary/10 ring-1 ring-primary/60'
                          : 'hover:bg-muted/40'
                    }`}
                    title={`${monthsFull[m.month - 1]}: ${Math.round(m.occupancy ?? 0)}% ocupación · ${m.median_price ? Math.round(m.median_price) + '€' : '—'}`}
                  >
                    {/* Badge de "HOY" sobre el mes actual */}
                    {isCurrent && (
                      <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-[8px] font-bold tracking-tight bg-primary text-primary-foreground px-1 py-0.2 rounded-full uppercase shadow">
                        {t('destination.todayBadge', 'Hoy')}
                      </span>
                    )}

                    {/* Icono de temporada sobre la barra */}
                    <span className="text-[11px] leading-none mb-0.5 opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-transform">
                      {season.icon}
                    </span>

                    {/* Barra de ocupación con altura calculada respecto al contenedor flex */}
                    <div className="w-full flex-1 flex items-end">
                      <div
                        className={`w-full rounded-t transition-all duration-200 ${
                          isSelected
                            ? 'bg-gradient-to-t from-amber-500 to-amber-300 shadow-md'
                            : isCurrent
                              ? 'bg-gradient-to-t from-sky-500 to-amber-400'
                              : 'bg-gradient-to-t from-amber-600/70 to-amber-400/80 group-hover:from-amber-500 group-hover:to-amber-300'
                        }`}
                        style={{ height: `${Math.max(8, pct)}%` }}
                      />
                    </div>

                    {/* Etiqueta del mes */}
                    <span
                      className={`text-[10px] font-medium transition-colors ${
                        isSelected
                          ? 'text-amber-300 font-bold'
                          : isCurrent
                            ? 'text-foreground font-bold'
                            : 'text-muted-foreground group-hover:text-foreground'
                      }`}
                    >
                      {monthsShort[m.month - 1]}
                    </span>
                  </button>
                )
              })}
            </div>

            {/* Panel de detalle del mes seleccionado */}
            {activeMonthData && (
              <div className="mt-3 pt-3 border-t border-border/40 bg-background/60 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-foreground flex items-center gap-1.5">
                      <span>{activeSeasonInfo.icon}</span>
                      {monthsFull[activeMonthData.month - 1]}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {activeSeasonInfo.tag[locale]}
                    </Badge>
                    {activeMonthData.month === currentMonth && (
                      <Badge variant="default" className="text-[10px]">
                        {t('destination.currentMonthBadge', 'Mes en curso')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <strong className="text-foreground/90 font-medium">{t('destination.natureLabel', 'Entorno natural: ')}</strong>
                    {activeSeasonInfo.nature[locale]}
                  </p>
                  <p className="text-xs text-amber-400/90">
                    <strong className="text-amber-400 font-medium">{t('destination.oppLabel', 'Oportunidad comercial: ')}</strong>
                    {activeSeasonInfo.opportunity[locale]}
                  </p>
                </div>

                <div className="flex items-center gap-4 shrink-0 bg-muted/30 px-3 py-2 rounded-lg border border-border/40">
                  <div>
                    <span className="text-[10px] uppercase text-muted-foreground block">{t('destination.occ', 'Ocupación est.')}</span>
                    <span className="text-sm font-bold font-mono text-amber-400">
                      {Math.round(activeMonthData.occupancy ?? 0)}%
                    </span>
                  </div>
                  <div className="h-6 w-px bg-border/60" />
                  <div>
                    <span className="text-[10px] uppercase text-muted-foreground block">{t('destination.price', 'Precio mediano')}</span>
                    <span className="text-sm font-bold font-mono text-emerald-400">
                      {activeMonthData.median_price ? `${Math.round(activeMonthData.median_price)}€` : '—'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-border/40">
          <ChartProvenanceCallout
            sourceName={t('chartExpl.destinationSignals.sourceName')}
            sourceType="mixed"
            sourceDesc={t('chartExpl.destinationSignals.sourceDesc')}
            insight={t('chartExpl.destinationSignals.insight')}
          />
        </div>
      </CardContent>
    </Card>
  )
}